import type { Id } from '@freightapi/core/ids'
import type { DocumentKind, MimeType } from '@freightapi/core/schemas/document'
import type { RemarkPhase, RemarkType } from '@freightapi/core/schemas/remark'
import type { ShipmentSnapshot, ShipmentStatus } from '@freightapi/core/schemas/shipment'
import type { SignatureMethod, SignatureRole, TrustLevel } from '@freightapi/core/schemas/signature'
import { type ApiUpload, apiFetch, apiUpload } from './api'
import type { SignLocale } from './locale'

/**
 * The wire shapes of the public sign endpoints (M1-10), mirrored for the browser.
 *
 * They are written out rather than imported from `apps/api`, because a Worker and a browser bundle
 * do not share a dependency: the app depends on the HTTP contract, not on the API's source tree.
 * `@freightapi/core` supplies the shared vocabulary (roles, statuses, the snapshot), so a change to
 * the canonical model still reaches this file through the compiler.
 */

/** The consignment note as `GET /v1/sign/:token` scopes it to the role of the link. */
export type SignShipment = Partial<Omit<ShipmentSnapshot, 'provider' | 'metadata'>> & {
  readonly id: Id<'shp'>
  readonly status: ShipmentStatus
  readonly version: number
  readonly version_id: Id<'ver'>
  /** The content this signature binds (ADR 0004). */
  readonly document_hash: string
  readonly issued_at?: string
}

export interface SignDocument {
  readonly id: Id<'doc'>
  readonly kind: DocumentKind
  readonly filename?: string
  readonly mime: MimeType
  readonly size: number
  readonly sha256: string
  /** A signed download URL that lives five minutes (ADR 0007). */
  readonly url: string
}

export interface SignExistingSignature {
  readonly role: SignatureRole
  readonly method: SignatureMethod
  readonly trust_level: TrustLevel
  readonly signer: { readonly name: string; readonly company?: string }
  readonly signed_at: string
}

/**
 * Another sign link of the same shipment (M2-06). Named, never handed over: a token is stored
 * hashed and left the API once, so this is enough to show the other role's row and nothing more.
 */
export interface SignRelatedRequest {
  readonly id: Id<'sgr'>
  readonly role: SignatureRole
  readonly status: SignRequestStatus
}

/**
 * A reservation as the sign context carries it: box 18 without the photos, because the pictures
 * are already in `documents` of the same payload, each with its own signed URL.
 */
export interface SignRemarkSummary {
  readonly id: Id<'rmk'>
  readonly phase: RemarkPhase
  readonly type: RemarkType
  readonly text?: string
  readonly created_at: string
  /** The organisation behind the API key, or the party behind the sign link that recorded it. */
  readonly author?: string
}

/** What a sign link can be, as `GET /v1/sign/:token` reports it; `revoked` arrived with M1-22. */
export type SignRequestStatus = 'pending' | 'used' | 'expired' | 'revoked'

/**
 * Where a one-time password can reach this signer (M2-02). Which of the two a link has depends on
 * the recipient it was created with, so the context answers the list and the signer picks (M2-04).
 */
export type OtpChannel = 'email' | 'sms'

export interface SignContext {
  readonly token_id: Id<'sgr'>
  readonly role: SignatureRole
  readonly status: 'pending' | 'used' | 'expired'
  readonly allowed_methods: readonly ('drawn' | 'click_otp')[]
  /** The channels a code can go to, best first; empty means `click_otp` has nowhere to send one. */
  readonly otp_channels: readonly OtpChannel[]
  readonly locale: string
  readonly expires_at: string
  readonly recipient_name?: string
  readonly shipment: SignShipment
  readonly signatures: readonly SignExistingSignature[]
  /** CMR box 18: the reservations on this note, newest first (M1-11). */
  readonly remarks: readonly SignRemarkSummary[]
  readonly documents: readonly SignDocument[]
  /** The other sign links of this shipment, without their tokens (M2-06). */
  readonly related_requests: readonly SignRelatedRequest[]
  /** What this link has to be worth; `platform_auth` unless the creator asked for more (M5-02). */
  readonly trust_level: TrustLevel
  /** Present exactly when `trust_level` is `ades` or `qes`: the extra step (M5-02). */
  readonly identification?: SignIdentification
  /** Present only when a handoff token resolved here: the sign token to submit with (M2-06). */
  readonly token?: string
}

/**
 * The identification step (M5-02, ADR 0017).
 *
 * This is deliberately the whole of what the sign PWA knows about trust service providers: a name
 * to show, a status to act on, and — when the provider has a flow of its own — a URL to send the
 * signer to. Which QTSP it is, what protocol it speaks and what ends up in the CAdES envelope is
 * the API's business, so replacing the mock with a real provider is not a release of this app.
 */
export interface SignIdentification {
  /** Names this identification for the status call; absent on the context, which starts nothing. */
  readonly reference?: string
  /** What the signer is told they identify themselves at, in so many words. */
  readonly provider_name: string
  readonly level: 'ades' | 'qes'
  readonly status: 'required' | 'pending' | 'verified'
  /** Present once `status` is `verified`; sent along with the signature. */
  readonly identification_token?: string
  /** Where the signer goes; absent means there is nowhere to send them and the step is done. */
  readonly redirect_url?: string
  readonly expires_at?: string
}

/** What `POST /v1/sign/:token/handoff` answers: a second link, for ten minutes and one call. */
export interface Handoff {
  readonly handoff_url: string
  readonly expires_at: string
  /** The QR code of `handoff_url` as an SVG document. */
  readonly qr_svg: string
}

/**
 * Whose screen the signature was drawn on (ADR 0004, M1-23). The one piece of evidence the signer
 * chooses: a consignee without a phone of their own signs on the driver device, and the evidence
 * has to say `driver` instead of quietly claiming `own`.
 */
export type SignDevice = 'own' | 'driver'

/** What `POST /v1/sign/:token/otp` answers: a code is on its way, and where it went. */
export interface OtpChallenge {
  readonly channel: OtpChannel
  /** The recipient, masked: the API never hands a public route a readable address. */
  readonly destination: string
  readonly expires_at: string
  /** How long to wait before offering a new code, so the window is not burnt. */
  readonly resend_in_seconds: number
}

/** What `POST /v1/sign/:token/otp/verify` answers: the single-use proof the signature takes. */
export interface OtpVerified {
  readonly otp_verified_token: string
  readonly expires_at: string
}

export interface DrawnSubmission {
  readonly method: 'drawn'
  readonly signer: { readonly name: string; readonly company?: string }
  /** Base64 PNG without the data URL prefix. */
  readonly image: string
  readonly geo?: { readonly lat: number; readonly lng: number; readonly accuracy_m?: number }
  /** Left out for `own`, which is what the API defaults to. */
  readonly device?: SignDevice
  readonly consent: true
  /** Only on an `ades` or `qes` link: the proof of `POST /v1/sign/:token/identification`. */
  readonly identification_token?: string
}

/**
 * Click-to-sign (M2-04, ADR 0004): no drawing, a verified one-time password instead. The proof is
 * bound to the address it was earned on, so the channel travels with it — the API needs it to know
 * which of the two inboxes to check the proof against, and the evidence records it.
 */
export interface OtpSubmission {
  readonly method: 'click_otp'
  readonly signer: { readonly name: string; readonly company?: string }
  readonly otp_verified_token: string
  readonly otp_channel?: OtpChannel
  /** Left out for `own`, which is what the API defaults to. */
  readonly device?: SignDevice
  readonly consent: true
  /** Only on an `ades` or `qes` link: the proof of `POST /v1/sign/:token/identification`. */
  readonly identification_token?: string
}

/** Either way of signing through a link; the method discriminates. */
export type SignSubmission = DrawnSubmission | OtpSubmission

/**
 * The photo ceiling per reservation, as the API enforces it (M1-11). Restated here for the same
 * reason the wire shapes above are: this app depends on the HTTP contract, not on the API's source
 * tree. The server counts the photos that are already waiting, so it has the last word either way.
 */
export const MAX_PHOTOS_PER_REMARK = 5

/** A photo of a reservation, as `POST /v1/sign/:token/photos` hands it back (M1-23). */
export interface SignPhoto {
  readonly id: Id<'doc'>
  readonly kind: DocumentKind
  readonly filename?: string
  readonly mime: MimeType
  readonly size: number
  readonly sha256: string
  /** A signed download URL that lives five minutes (ADR 0007). */
  readonly download_url: string
}

/** A reservation at pickup or delivery (CMR box 18 and 24), with its photos resolved. */
export interface SignRemark {
  readonly id: Id<'rmk'>
  readonly shipment_id: Id<'shp'>
  readonly phase: RemarkPhase
  readonly type: RemarkType
  readonly text?: string
  readonly photo_document_ids: readonly Id<'doc'>[]
  readonly photos: readonly SignPhoto[]
  readonly created_at: string
}

export interface CreateRemarkBody {
  readonly phase: RemarkPhase
  readonly type: RemarkType
  readonly text?: string
  readonly photo_document_ids?: readonly Id<'doc'>[]
}

/** The signature as `POST /v1/sign/:token` records it; the flow only needs its timestamp. */
export interface SignatureResource {
  readonly id: Id<'sig'>
  readonly shipment_id: Id<'shp'>
  readonly role: SignatureRole
  readonly method: SignatureMethod
  readonly trust_level: TrustLevel
  readonly signer: { readonly name: string; readonly company?: string }
  readonly evidence: { readonly signed_at: string }
  readonly image_document_id?: Id<'doc'>
}

export function loadSignContext(
  token: string,
  options: { readonly locale: SignLocale; readonly signal?: AbortSignal },
): Promise<SignContext> {
  return apiFetch<SignContext>(`/v1/sign/${encodeURIComponent(token)}`, {
    locale: options.locale,
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  })
}

export function submitSignature(
  token: string,
  body: SignSubmission,
  options: { readonly locale: SignLocale },
): Promise<SignatureResource> {
  return apiFetch<SignatureResource>(`/v1/sign/${encodeURIComponent(token)}`, {
    method: 'POST',
    body,
    locale: options.locale,
  })
}

/**
 * Asks for a one-time password over `channel` (M2-04). The API answers the masked destination, so
 * the screen can say which inbox to look in without ever learning the address itself.
 */
export function requestSignOtp(
  token: string,
  channel: OtpChannel,
  options: { readonly locale: SignLocale },
): Promise<OtpChallenge> {
  return apiFetch<OtpChallenge>(`/v1/sign/${encodeURIComponent(token)}/otp?channel=${channel}`, {
    method: 'POST',
    locale: options.locale,
  })
}

/**
 * Exchanges the six digits for the proof `POST /v1/sign/:token` accepts. The channel is part of the
 * address, so it is sent along: a code earned on a phone number cannot be verified against a mailbox.
 */
export function verifySignOtp(
  token: string,
  input: { readonly code: string; readonly channel: OtpChannel },
  options: { readonly locale: SignLocale },
): Promise<OtpVerified> {
  return apiFetch<OtpVerified>(
    `/v1/sign/${encodeURIComponent(token)}/otp/verify?channel=${input.channel}`,
    { method: 'POST', body: { code: input.code }, locale: options.locale },
  )
}

/**
 * Sends the signer off to identify themselves (M5-02). The answer is either `verified` with the
 * token the signature needs, or `pending` with somewhere to go; this app treats both the same way
 * and never learns which provider was behind it.
 */
export function startIdentification(
  token: string,
  body: { readonly signer?: { readonly name?: string; readonly company?: string } },
  options: { readonly locale: SignLocale },
): Promise<SignIdentification> {
  return apiFetch<SignIdentification>(`/v1/sign/${encodeURIComponent(token)}/identification`, {
    method: 'POST',
    body,
    locale: options.locale,
  })
}

/** Polls an identification the signer went away for; reading does not use it up. */
export function readIdentification(
  token: string,
  reference: string,
  options: { readonly locale: SignLocale; readonly signal?: AbortSignal },
): Promise<SignIdentification> {
  const query = `reference=${encodeURIComponent(reference)}`
  return apiFetch<SignIdentification>(
    `/v1/sign/${encodeURIComponent(token)}/identification?${query}`,
    {
      locale: options.locale,
      ...(options.signal === undefined ? {} : { signal: options.signal }),
    },
  )
}

/**
 * Moves the session to another device (M2-06): a second token with its QR code, good for ten
 * minutes and for one call, that resolves to this very sign link.
 */
export function createHandoff(
  token: string,
  options: { readonly locale: SignLocale; readonly signal?: AbortSignal },
): Promise<Handoff> {
  return apiFetch<Handoff>(`/v1/sign/${encodeURIComponent(token)}/handoff`, {
    method: 'POST',
    locale: options.locale,
    ...(options.signal === undefined ? {} : { signal: options.signal }),
  })
}

/**
 * One photo of a reservation (M1-23). They go up one call at a time so a dropped connection costs
 * a single picture instead of the whole set, and so the bar under each thumbnail belongs to one
 * file. The server counts what is already waiting, so the five-photo ceiling holds regardless.
 */
export async function uploadSignPhoto(
  token: string,
  photo: File,
  options: ApiUpload = {},
): Promise<SignPhoto> {
  const form = new FormData()
  form.append('photo', photo, photo.name)
  const body = await apiUpload<{ photos: readonly SignPhoto[] }>(
    `/v1/sign/${encodeURIComponent(token)}/photos`,
    form,
    options,
  )
  const stored = body.photos[0]
  if (stored === undefined) throw new Error('The API accepted the photo but returned none')
  return stored
}

/**
 * The reservation itself (M1-23), with the ids of the photos that were uploaded for it. The link is
 * not used up by this, so the signature still follows — but a signed link is spent, which is why
 * the flow records the reservation before anybody draws anything.
 */
export function createSignRemark(
  token: string,
  body: CreateRemarkBody,
  options: { readonly locale: SignLocale },
): Promise<SignRemark> {
  return apiFetch<SignRemark>(`/v1/sign/${encodeURIComponent(token)}/remarks`, {
    method: 'POST',
    body,
    locale: options.locale,
  })
}

/**
 * Strips the `data:` prefix a canvas export carries. The API accepts either form, but sending the
 * prefix wastes bytes on a mobile connection and makes the size budget harder to reason about.
 */
export function base64Of(dataUrl: string): string {
  const comma = dataUrl.indexOf(',')
  return comma === -1 ? dataUrl : dataUrl.slice(comma + 1)
}
